/**
 * #cell-status channel — live read-only panel for the Pi SIM modem.
 *
 * hub-cell-edge POSTs a signed snapshot every 10s to the backend; we show the
 * latest one here with a header card (carrier / MSISDN / LTE badge / signal
 * bars / registration dot / READ-ONLY pill), a detail grid (ICCID/IMSI/IMEI/
 * APN/cell ID/RSRP/RSRQ/SINR/temp/uptime), and a 2h signal sparkline. The
 * SSE stream pushes live updates. When no snapshot has landed in the last 60s
 * we flip to an "offline" empty state so the dashboard doesn't silently serve
 * stale cell-status on a dead modem.
 */

import { get, API_BASE } from '../api';
import { state } from '../state';
import { realtime, channelTopic, type RealtimeEvent } from '../realtime';

interface SimSnapshot {
  iccid?: string;
  imsi?: string;
  msisdn?: string | null;
  carrier?: string;
  registration?: string;
  network_mode?: string;
  band?: string;
  cell_id?: string;
  apn?: string;
}

interface SignalSnapshot {
  csq_rssi?: number;
  csq_ber?: number;
  rsrp?: number | null;
  rsrq?: number | null;
  sinr?: number | null;
}

interface DeviceSnapshot {
  model?: string;
  firmware?: string;
  imei?: string;
  temp_celsius?: number | null;
  uptime_secs?: number;
}

interface CellCounters {
  inbound_sms_today?: number;
  outbound_sms_today?: number;
  inbound_calls_today?: number;
  missed_calls_today?: number;
  inbound_mms_today?: number;
}

interface CellSnapshot {
  sim?: SimSnapshot;
  signal?: SignalSnapshot;
  device?: DeviceSnapshot;
  counters?: CellCounters;
  read_only?: boolean;
  edge_version?: string;
  generated_at?: string;
  _received_at?: string;
}

interface CurrentResponse {
  server_id: string;
  received_at: string | null;
  snapshot: CellSnapshot | null;
}

interface HistoryItem {
  received_at: string;
  snapshot: CellSnapshot;
}

interface HistoryResponse {
  server_id: string;
  items: HistoryItem[];
}

/** Message is "offline" if the snapshot is older than this. */
const OFFLINE_THRESHOLD_MS = 60_000;

let unsubRealtime: (() => void) | null = null;
let currentTopic: string | null = null;
let offlineTimer: ReturnType<typeof setInterval> | null = null;
let currentSnapshot: CellSnapshot | null = null;
let currentReceivedAt: string | null = null;
let history: HistoryItem[] = [];
let webhookConfigured: boolean | null = null;
let callWebhookConfigured: boolean | null = null;
let callsReadOnly: boolean | null = null;

export function cleanupCellStatus(): void {
  if (unsubRealtime) { unsubRealtime(); unsubRealtime = null; }
  if (currentTopic) { realtime.unsubscribeChannel(currentTopic); currentTopic = null; }
  if (offlineTimer) { clearInterval(offlineTimer); offlineTimer = null; }
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, c => (
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;'
  ));
}

/** Map RSSI (0-31, 99=unknown) to a 0-4 bars scale. Quick heuristic per
 *  ETSI TS 127.007: <10 weak, 10-14 fair, 15-19 good, 20+ excellent. */
function rssiToBars(rssi: number | undefined): number {
  if (rssi == null || rssi === 99) return 0;
  if (rssi < 5) return 1;
  if (rssi < 10) return 2;
  if (rssi < 15) return 3;
  if (rssi < 20) return 4;
  return 5;
}

function renderBars(bars: number): string {
  let out = '';
  for (let i = 1; i <= 5; i++) {
    const lit = i <= bars;
    const h = 4 + i * 2;
    const bg = lit ? 'var(--accent)' : 'var(--border)';
    out += `<span style="display:inline-block;width:4px;height:${h}px;margin-right:2px;background:${bg};vertical-align:bottom;border-radius:1px"></span>`;
  }
  return `<span title="CSQ bars" style="display:inline-flex;align-items:flex-end;height:16px">${out}</span>`;
}

function registrationDot(reg: string | undefined): string {
  const r = (reg || '').toLowerCase();
  let color = 'var(--fg-muted)';
  let label = reg || 'unknown';
  if (r.includes('registered-home')) { color = '#16a34a'; label = 'home'; }
  else if (r.includes('registered-roaming')) { color = '#f59e0b'; label = 'roaming'; }
  else if (r === 'searching') { color = '#3b82f6'; label = 'searching'; }
  else if (r === 'denied') { color = '#dc2626'; label = 'denied'; }
  else if (r === 'not-registered') { color = 'var(--fg-muted)'; label = 'not registered'; }
  return `<span style="display:inline-flex;align-items:center;gap:6px;font-size:12px;color:var(--fg-muted)"><span style="display:inline-block;width:8px;height:8px;border-radius:4px;background:${color}"></span>${escape(label)}</span>`;
}

function formatUptime(secs: number | undefined): string {
  if (!secs || secs <= 0) return '—';
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function isOffline(): boolean {
  if (!currentReceivedAt) return true;
  const t = Date.parse(currentReceivedAt);
  if (isNaN(t)) return true;
  return Date.now() - t > OFFLINE_THRESHOLD_MS;
}

function staleDuration(): string {
  if (!currentReceivedAt) return '';
  const t = Date.parse(currentReceivedAt);
  if (isNaN(t)) return '';
  const delta = Math.floor((Date.now() - t) / 1000);
  if (delta < 120) return `${delta}s ago`;
  if (delta < 7200) return `${Math.floor(delta / 60)}m ago`;
  if (delta < 172800) return `${Math.floor(delta / 3600)}h ago`;
  return `${Math.floor(delta / 86400)}d ago`;
}

type EmptyReason = 'no-data' | 'offline' | 'not-configured';

function emptyState(container: HTMLElement, reason: EmptyReason): void {
  let title = 'Cell Status';
  let msg: string;
  let detail: string;
  const stale = staleDuration();

  if (reason === 'not-configured') {
    msg = 'Cell status webhook is not configured on the server.';
    detail = 'Set HUB_CELL_STATUS_SECRET on both hub-cell and hub-people containers, then restart.';
  } else if (reason === 'offline' && stale) {
    msg = `Last snapshot received ${escape(stale)}. No new data arriving.`;
    detail = 'Hub Cell may have lost connectivity, or the status publisher stopped. Check hub-cell container logs.';
  } else if (reason === 'offline') {
    msg = 'No snapshots have been received from Hub Cell.';
    detail = 'The publisher may not be running. Check that HUB_CELL_STATUS_SECRET is set on hub-cell and the container can reach hub-people.';
  } else {
    msg = 'Waiting for the first snapshot from hub-cell-edge…';
    detail = 'The Pi posts an HMAC-signed status every 10 s.';
  }

  const diagnosticHtml = buildDiagnosticBadges();

  container.innerHTML = `
    <div style="padding:40px 20px;text-align:center;color:var(--fg-muted);font-family:var(--font-sans)">
      <div style="font-size:18px;margin-bottom:8px">${escape(title)}</div>
      <div style="font-size:13px;max-width:480px;margin:0 auto">${escape(msg)}</div>
      <div style="font-size:11px;margin-top:10px;opacity:0.7;max-width:480px;margin-left:auto;margin-right:auto">${escape(detail)}</div>
      ${diagnosticHtml}
    </div>
  `;
}

function buildDiagnosticBadges(): string {
  const badges: string[] = [];
  const dot = (ok: boolean | null) =>
    ok === true ? '#16a34a' : ok === false ? '#dc2626' : 'var(--fg-muted)';
  const label = (ok: boolean | null) =>
    ok === true ? 'configured' : ok === false ? 'not set' : 'unknown';

  badges.push(
    `<span style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border:1px solid var(--border);border-radius:4px;font-size:11px">` +
    `<span style="width:6px;height:6px;border-radius:3px;background:${dot(webhookConfigured)}"></span>` +
    `Status secret: ${label(webhookConfigured)}</span>`
  );
  badges.push(
    `<span style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border:1px solid var(--border);border-radius:4px;font-size:11px">` +
    `<span style="width:6px;height:6px;border-radius:3px;background:${dot(callWebhookConfigured)}"></span>` +
    `Call webhook: ${label(callWebhookConfigured)}</span>`
  );
  if (callsReadOnly === true) {
    badges.push(
      `<span style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border:1px solid var(--border);border-radius:4px;font-size:11px;background:#dc2626;color:#fff">` +
      `CALLS READ-ONLY</span>`
    );
  }

  return `<div style="display:flex;flex-wrap:wrap;gap:6px;justify-content:center;margin-top:16px">${badges.join('')}</div>`;
}

/** Build the header card + detail grid + sparkline. */
function renderPanel(container: HTMLElement): void {
  if (!currentSnapshot) {
    if (webhookConfigured === false) {
      emptyState(container, 'not-configured');
    } else {
      emptyState(container, 'no-data');
    }
    return;
  }
  if (isOffline()) {
    emptyState(container, 'offline');
    return;
  }

  const sim = currentSnapshot.sim || {};
  const sig = currentSnapshot.signal || {};
  const dev = currentSnapshot.device || {};
  const ct = currentSnapshot.counters || {};
  const bars = rssiToBars(sig.csq_rssi);
  const readOnly = currentSnapshot.read_only === true;
  const receivedAt = currentReceivedAt || currentSnapshot.generated_at || '';
  const mode = (sim.network_mode || '').toUpperCase();

  container.innerHTML = `
    <div style="padding:16px;overflow-y:auto;flex:1;min-height:0;font-family:var(--font-sans)">
      <div class="cs-card" style="border:1px solid var(--border);border-radius:8px;padding:14px 16px;background:var(--bg-elev)">
        <div style="display:flex;gap:12px;align-items:center;flex-wrap:wrap">
          <div style="font-weight:700;font-size:16px;color:var(--fg)">${escape(sim.carrier || 'Unknown carrier')}</div>
          <div style="color:var(--fg-muted);font-size:13px">${escape(sim.msisdn || '')}</div>
          ${mode ? `<span style="padding:2px 8px;border-radius:3px;background:var(--accent);color:var(--bg);font-size:11px;font-weight:700;letter-spacing:0.4px">${escape(mode)}</span>` : ''}
          ${renderBars(bars)}
          ${registrationDot(sim.registration)}
          ${readOnly ? `<span title="HUB_CELL_READ_ONLY kill switch is active" style="margin-left:auto;padding:2px 8px;border-radius:3px;background:#dc2626;color:#fff;font-size:11px;font-weight:700;letter-spacing:0.4px">READ-ONLY</span>` : `<span style="margin-left:auto"></span>`}
        </div>
        <div style="color:var(--fg-muted);font-size:11px;margin-top:8px">
          last update ${escape(receivedAt)} · edge ${escape(currentSnapshot.edge_version || '?')}
        </div>
      </div>

      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:10px;margin-top:14px">
        ${gridItem('ICCID', sim.iccid || '—')}
        ${gridItem('IMSI', sim.imsi || '—')}
        ${gridItem('IMEI', dev.imei || '—')}
        ${gridItem('APN', sim.apn || '—')}
        ${gridItem('Cell ID', sim.cell_id || '—')}
        ${gridItem('Band', sim.band || '—')}
        ${gridItem('RSRP', sig.rsrp != null ? `${sig.rsrp} dBm` : '—')}
        ${gridItem('RSRQ', sig.rsrq != null ? `${sig.rsrq} dB` : '—')}
        ${gridItem('SINR', sig.sinr != null ? `${sig.sinr} dB` : '—')}
        ${gridItem('CSQ RSSI', sig.csq_rssi != null ? `${sig.csq_rssi}` : '—')}
        ${gridItem('Temperature', dev.temp_celsius != null ? `${dev.temp_celsius} °C` : '—')}
        ${gridItem('Uptime', formatUptime(dev.uptime_secs))}
        ${gridItem('Model', dev.model || '—')}
        ${gridItem('Firmware', dev.firmware || '—')}
      </div>

      <div style="margin-top:18px">
        <div style="color:var(--fg-muted);font-size:11px;text-transform:uppercase;letter-spacing:0.4px;margin-bottom:6px">
          Signal · last 2 h
        </div>
        <div id="cs-spark" style="width:100%;height:72px;border:1px solid var(--border);border-radius:6px;padding:6px 8px;background:var(--bg-elev);box-sizing:border-box">
          ${renderSparkline()}
        </div>
      </div>

      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:10px;margin-top:18px">
        ${counterItem('SMS in', ct.inbound_sms_today)}
        ${counterItem('SMS out', ct.outbound_sms_today)}
        ${counterItem('Calls in', ct.inbound_calls_today)}
        ${counterItem('Missed', ct.missed_calls_today)}
        ${counterItem('MMS in', ct.inbound_mms_today)}
      </div>

      <div style="color:var(--fg-muted);font-size:11px;text-align:center;margin-top:18px">
        Read-only mirror. Send SMS via hub-cell. Kill switch: <code>HUB_CELL_READ_ONLY=1</code>.
      </div>
      ${buildDiagnosticBadges()}
    </div>
  `;
}

function gridItem(label: string, value: string): string {
  return `
    <div style="border:1px solid var(--border);border-radius:6px;padding:8px 10px;background:var(--bg)">
      <div style="color:var(--fg-muted);font-size:10px;text-transform:uppercase;letter-spacing:0.4px;margin-bottom:4px">${escape(label)}</div>
      <div style="color:var(--fg);font-size:13px;font-family:var(--font-mono,monospace);word-break:break-all">${escape(value)}</div>
    </div>
  `;
}

function counterItem(label: string, n: number | undefined): string {
  const v = typeof n === 'number' ? n.toString() : '0';
  return `
    <div style="border:1px solid var(--border);border-radius:6px;padding:10px 12px;background:var(--bg);text-align:center">
      <div style="color:var(--fg-muted);font-size:10px;text-transform:uppercase;letter-spacing:0.4px;margin-bottom:4px">${escape(label)} · today</div>
      <div style="color:var(--fg);font-size:20px;font-weight:700">${escape(v)}</div>
    </div>
  `;
}

function renderSparkline(): string {
  if (history.length < 2) {
    return '<div style="color:var(--fg-muted);font-size:11px;padding:20px 0;text-align:center">Collecting samples…</div>';
  }
  const W = 600; // viewBox width — scales via CSS
  const H = 60;
  // Pick an RSSI-like metric per-row: prefer RSRP (dBm negative), else CSQ.
  const samples = history.map(h => {
    const s = h.snapshot.signal || {};
    if (typeof s.rsrp === 'number') return s.rsrp;
    if (typeof s.csq_rssi === 'number' && s.csq_rssi !== 99) return -110 + s.csq_rssi * 2;
    return null;
  });
  const valid = samples.filter((v): v is number => typeof v === 'number');
  if (valid.length < 2) {
    return '<div style="color:var(--fg-muted);font-size:11px;padding:20px 0;text-align:center">Collecting samples…</div>';
  }
  const min = Math.min(...valid, -120);
  const max = Math.max(...valid, -70);
  const range = Math.max(1, max - min);
  const n = samples.length;
  const pts = samples.map((v, i) => {
    if (v == null) return '';
    const x = (i / (n - 1)) * W;
    const y = H - ((v - min) / range) * (H - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).filter(Boolean).join(' ');
  return `
    <svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" style="width:100%;height:100%">
      <polyline points="${pts}" fill="none" stroke="var(--accent)" stroke-width="1.5" />
    </svg>
  `;
}

interface HealthResponse {
  ok: boolean;
  configured: boolean;
}

interface CallHealthResponse {
  ok: boolean;
  configured: boolean;
  calls_read_only: boolean;
}

async function loadInitial(serverId: string): Promise<void> {
  try {
    const [cur, hist, statusHealth, callHealth] = await Promise.all([
      get<CurrentResponse>(`${API_BASE}/api/cell-status?server_id=${encodeURIComponent(serverId)}`),
      get<HistoryResponse>(`${API_BASE}/api/cell-status/history?server_id=${encodeURIComponent(serverId)}&limit=500`),
      get<HealthResponse>(`${API_BASE}/api/cell-status/health`).catch(() => null),
      get<CallHealthResponse>(`${API_BASE}/api/webhook/call-event/health`).catch(() => null),
    ]);
    currentSnapshot = cur.snapshot;
    currentReceivedAt = cur.received_at;
    history = hist.items;
    webhookConfigured = statusHealth?.configured ?? null;
    callWebhookConfigured = callHealth?.configured ?? null;
    callsReadOnly = callHealth?.calls_read_only ?? null;
  } catch {
    currentSnapshot = null;
    currentReceivedAt = null;
    history = [];
  }
}

export async function renderCellStatus(container: HTMLElement): Promise<void> {
  cleanupCellStatus();
  currentSnapshot = null;
  currentReceivedAt = null;
  history = [];

  const sid = state.currentServer;
  if (!sid) {
    container.innerHTML = '<div style="padding:20px;color:var(--fg-muted)">Select a server to view cell status.</div>';
    return;
  }

  container.innerHTML = `
    <div style="flex:1;min-height:0;display:flex;flex-direction:column">
      <div id="cs-root" style="flex:1;min-height:0;display:flex;flex-direction:column"></div>
    </div>
  `;
  const root = container.querySelector<HTMLElement>('#cs-root');
  if (!root) return;

  emptyState(root, 'no-data');
  await loadInitial(sid);
  renderPanel(root);

  const topic = channelTopic(sid, 'cell-status');
  currentTopic = topic;
  realtime.connect();
  realtime.subscribeChannel(topic);
  unsubRealtime = realtime.on((ev: RealtimeEvent) => {
    if (ev.type !== 'cell_status_updated' || ev.channel_id !== topic) return;
    const snap = ev.snapshot as unknown as CellSnapshot;
    currentSnapshot = snap;
    const received = snap._received_at || snap.generated_at || new Date().toISOString();
    currentReceivedAt = received;
    history = [...history, { received_at: received, snapshot: snap }].slice(-500);
    renderPanel(root);
  });

  // Offline watcher — re-renders every 20s so the empty-state flips to
  // "offline" automatically when snapshots stop arriving.
  offlineTimer = setInterval(() => renderPanel(root), 20_000);
}
